import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  AddRounded as AddIcon,
  SearchRounded as SearchIcon,
  EditRounded as EditIcon,
  DeleteOutlineRounded as DeleteIcon,
  GroupsRounded as GroupsIcon,
  BusinessRounded as BusinessIcon,
  VerifiedUserRounded as ShieldIcon,
  CalendarMonthRounded as CalendarIcon,
  CloseRounded as CloseIcon,
  RefreshRounded as RefreshIcon,
  PersonRounded as PersonIcon,
  FamilyRestroomRounded as FamilyIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import api from '../../services/api';
import { EmptyState, LoadingState, PageHeader, PageLayout } from '../../components/ui';
import {
  POLICY_STATUS_META,
  asClassifications,
  expiryHint,
  formatCoverage,
  getPolicyStatus,
  policyDateRange,
} from '../../utils/insuranceStatus';

const EMPTY_FORM = { name: '', provider_name: '', coverage_amount: '', start_date: '', end_date: '', details: '' };

function StatusChip({ status }) {
  const theme = useTheme();
  const meta = POLICY_STATUS_META[status] || POLICY_STATUS_META.active;
  const color = theme.palette?.[meta.tone]?.main || theme.palette.text.secondary;
  const textColor = meta.tone === 'secondary' ? theme.palette.text.secondary : theme.palette[meta.tone]?.dark || color;
  return (
    <Chip
      size="small"
      label={meta.label}
      sx={{ borderRadius: '8px', backgroundColor: alpha(color, 0.14), color: textColor, fontWeight: 600 }}
    />
  );
}

function Tile({ label, value, tone }) {
  const theme = useTheme();
  const color = tone ? theme.palette?.[tone]?.main : theme.palette.text.primary;
  return (
    <Box sx={{ p: 1.1, borderRadius: '10px', backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
      <Typography variant="caption" sx={{ fontWeight: 700, color: tone ? color : 'text.secondary' }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1, color: tone ? color : 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Typography>
    </Box>
  );
}

function ClassificationEditor({ value, onChange }) {
  const [input, setInput] = useState('');
  const list = asClassifications(value);
  const add = () => {
    const v = input.trim();
    if (!v || list.includes(v)) {
      setInput('');
      return;
    }
    onChange([...list, v]);
    setInput('');
  };
  return (
    <Box>
      <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap" sx={{ mb: list.length ? 1 : 0 }}>
        {list.map((c) => (
          <Chip key={c} label={c} size="small" onDelete={() => onChange(list.filter((x) => x !== c))} sx={{ borderRadius: '8px' }} />
        ))}
      </Stack>
      <Stack direction="row" spacing={1}>
        <TextField
          size="small"
          label="Add classification"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
          fullWidth
        />
        <Button variant="outlined" onClick={add}>
          Add
        </Button>
      </Stack>
    </Box>
  );
}

function PolicyDialog({ open, mode, initial, onClose, onSave }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [classes, setClasses] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        name: initial?.name || '',
        provider_name: initial?.provider_name || '',
        coverage_amount: initial?.coverage_amount ?? '',
        start_date: initial?.start_date ? String(initial.start_date).slice(0, 10) : '',
        end_date: initial?.end_date ? String(initial.end_date).slice(0, 10) : '',
        details: initial?.details || '',
      });
      setClasses(asClassifications(initial?.classifications));
    }
  }, [open, initial]);

  const change = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error('Policy name is required.');
      return;
    }
    if (form.start_date && form.end_date && form.end_date < form.start_date) {
      toast.error('End date must be on or after the start date.');
      return;
    }
    setSaving(true);
    try {
      await onSave({ ...form, coverage_amount: form.coverage_amount === '' ? null : form.coverage_amount, classifications: classes });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>{mode === 'edit' ? 'Edit policy' : 'New policy'}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ pt: 0.5 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField fullWidth required label="Policy name" name="name" value={form.name} onChange={change} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField fullWidth label="Provider" name="provider_name" value={form.provider_name} onChange={change} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField fullWidth label="Coverage amount" name="coverage_amount" type="number" value={form.coverage_amount} onChange={change} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField fullWidth label="Start date" name="start_date" type="date" InputLabelProps={{ shrink: true }} value={form.start_date} onChange={change} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <TextField fullWidth label="End date" name="end_date" type="date" InputLabelProps={{ shrink: true }} value={form.end_date} onChange={change} />
          </Grid>
          <Grid size={12}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.8 }}>
              Classifications
            </Typography>
            <ClassificationEditor value={classes} onChange={setClasses} />
          </Grid>
          <Grid size={12}>
            <TextField fullWidth multiline minRows={3} label="Details" name="details" value={form.details} onChange={change} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={saving}>
          {saving ? 'Saving…' : mode === 'edit' ? 'Save changes' : 'Create policy'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CoveredDialog({ open, policy, rows, onClose }) {
  const employees = (rows || []).filter((r) => r.type !== 'family');
  const family = (rows || []).filter((r) => r.type === 'family');

  const Row = ({ r }) => (
    <Stack direction="row" spacing={1.2} alignItems="center" sx={{ py: 0.7 }}>
      <Avatar sx={{ width: 32, height: 32, bgcolor: 'background.default', color: 'text.secondary' }}>
        {r.type === 'family' ? <FamilyIcon fontSize="small" /> : <PersonIcon fontSize="small" />}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
          {r.name}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {r.subtitle || (r.type === 'family' ? 'Family member' : 'Employee')}
        </Typography>
      </Box>
      {r.classification ? <Chip size="small" label={r.classification} sx={{ borderRadius: '8px' }} /> : null}
    </Stack>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Covered members
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {policy?.name}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {rows == null ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        ) : rows.length === 0 ? (
          <EmptyState compact title="Nobody covered yet" description="No employees or family members are assigned to this policy." />
        ) : (
          <Stack spacing={1.5}>
            {employees.length ? (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                  Employees ({employees.length})
                </Typography>
                {employees.map((r) => (
                  <Row key={`e-${r.assignment_id}`} r={r} />
                ))}
              </Box>
            ) : null}
            {family.length ? (
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
                  Family members ({family.length})
                </Typography>
                {family.map((r) => (
                  <Row key={`f-${r.assignment_id}`} r={r} />
                ))}
              </Box>
            ) : null}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

function PolicyCard({ policy, onEdit, onCovered, onDelete }) {
  const theme = useTheme();
  const status = getPolicyStatus(policy);
  const meta = POLICY_STATUS_META[status];
  const railColor = theme.palette?.[meta.tone]?.main || theme.palette.text.secondary;
  const classes = asClassifications(policy.classifications);
  const covered = Number(policy.assignments_count) || 0;
  const hint = expiryHint(policy);

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1.5,
        borderRadius: '12px',
        border: '1px solid',
        borderColor: theme.hr?.line?.soft || 'divider',
        backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.7) : alpha(theme.palette.background.paper, 0.96),
        overflow: 'hidden',
      }}
    >
      <Box sx={{ width: 4, flexShrink: 0, backgroundColor: railColor }} />
      <Box sx={{ flex: 1, minWidth: 0, p: 1.3 }}>
        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
              {policy.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              <BusinessIcon sx={{ fontSize: 14, verticalAlign: '-2px', mr: 0.4 }} />
              {policy.provider_name || 'No provider'}
            </Typography>
          </Box>
          <StatusChip status={status} />
        </Stack>

        <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 1 }}>
          <Chip
            size="small"
            icon={<ShieldIcon sx={{ fontSize: 15 }} />}
            label={formatCoverage(policy.coverage_amount)}
            sx={{ borderRadius: '8px', '& .MuiChip-label': { fontVariantNumeric: 'tabular-nums', fontWeight: 600 } }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            <CalendarIcon sx={{ fontSize: 14, verticalAlign: '-2px', mr: 0.4 }} />
            {policyDateRange(policy)}
          </Typography>
          {hint ? (
            <Typography variant="caption" sx={{ color: status === 'expiring' ? 'warning.main' : 'text.disabled', fontVariantNumeric: 'tabular-nums' }}>
              · {hint}
            </Typography>
          ) : null}
        </Stack>

        <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 0.9 }}>
          <Chip
            size="small"
            icon={<GroupsIcon sx={{ fontSize: 15 }} />}
            label={`${covered} covered`}
            onClick={() => onCovered(policy)}
            sx={{ borderRadius: '8px', cursor: 'pointer', '& .MuiChip-label': { fontVariantNumeric: 'tabular-nums', fontWeight: 600 } }}
          />
          {classes.map((c) => (
            <Chip key={c} size="small" variant="outlined" label={c} sx={{ borderRadius: '8px' }} />
          ))}
        </Stack>

        <Stack direction="row" spacing={0.8} sx={{ mt: 1.2 }}>
          <Button size="small" startIcon={<EditIcon />} onClick={() => onEdit(policy)}>
            Edit
          </Button>
          <Button size="small" startIcon={<GroupsIcon />} onClick={() => onCovered(policy)}>
            Assignments
          </Button>
          <Tooltip title="Delete policy">
            <IconButton size="small" color="error" sx={{ ml: 'auto' }} onClick={() => onDelete(policy)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    </Box>
  );
}

export default function InsurancePolicyList() {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(() => {
    const s = searchParams.get('status');
    return ['active', 'expiring', 'expired'].includes(s) ? s : 'all';
  });

  const [dialog, setDialog] = useState({ open: false, mode: 'create', policy: null });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [covered, setCovered] = useState({ open: false, policy: null, rows: null });

  async function load() {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/admin/insurance-policies');
      setPolicies(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setError('Could not load insurance policies.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const stats = useMemo(() => {
    let active = 0;
    let expiring = 0;
    let coveredTotal = 0;
    policies.forEach((p) => {
      const s = getPolicyStatus(p);
      if (s === 'active') active += 1;
      if (s === 'expiring') expiring += 1;
      coveredTotal += Number(p.assignments_count) || 0;
    });
    return { total: policies.length, active, expiring, coveredTotal };
  }, [policies]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    return policies.filter((p) => {
      if (statusFilter !== 'all' && getPolicyStatus(p) !== statusFilter) return false;
      if (term && !`${p.name} ${p.provider_name || ''}`.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [policies, search, statusFilter]);

  async function savePolicy(payload) {
    const isEdit = dialog.mode === 'edit' && dialog.policy;
    try {
      if (isEdit) {
        const { data } = await api.put(`/admin/insurance-policies/${dialog.policy.id}`, payload);
        setPolicies((prev) => prev.map((p) => (p.id === dialog.policy.id ? { ...p, ...data } : p)));
        toast.success('Policy updated.');
      } else {
        const { data } = await api.post('/admin/insurance-policies', payload);
        setPolicies((prev) => [{ assignments_count: 0, ...data }, ...prev]);
        toast.success('Policy created.');
      }
      setDialog({ open: false, mode: 'create', policy: null });
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || 'Could not save the policy.');
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/insurance-policies/${deleteTarget.id}`);
      setPolicies((prev) => prev.filter((p) => p.id !== deleteTarget.id));
      toast.success('Policy deleted.');
      setDeleteTarget(null);
    } catch (err) {
      console.error(err);
      toast.error('Could not delete the policy.');
    } finally {
      setDeleting(false);
    }
  }

  async function openCovered(policy) {
    setCovered({ open: true, policy, rows: null });
    try {
      const { data } = await api.get(`/admin/insurance-policies/${policy.id}/assignments`);
      setCovered({ open: true, policy, rows: Array.isArray(data) ? data : [] });
    } catch (err) {
      console.error(err);
      setCovered({ open: true, policy, rows: [] });
      toast.error('Could not load covered members.');
    }
  }

  return (
    <PageLayout backgroundAccent compact>
      <PageHeader
        compact
        overline="HR admin"
        title="Insurance policies"
        subtitle="Manage providers, coverage, classifications and who's covered."
        actions={[
          <Button key="refresh" variant="outlined" startIcon={<RefreshIcon />} onClick={load}>
            Refresh
          </Button>,
          <Button key="new" variant="contained" startIcon={<AddIcon />} onClick={() => setDialog({ open: true, mode: 'create', policy: null })}>
            New policy
          </Button>,
        ]}
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 1.5, borderRadius: '12px' }} onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' }, gap: 1, mb: 1.5 }}>
        <Tile label="Policies" value={stats.total} />
        <Tile label="Active" value={stats.active} tone="success" />
        <Tile label="Expiring ≤30d" value={stats.expiring} tone="warning" />
        <Tile label="Covered employees" value={stats.coveredTotal} />
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} justifyContent="space-between" sx={{ mb: 1.4 }}>
        <TextField
          size="small"
          placeholder="Search name or provider"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: { xs: '100%', sm: 280 } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={statusFilter}
          onChange={(_, v) => v && setStatusFilter(v)}
          sx={{ '& .MuiToggleButton-root': { borderRadius: '999px !important', textTransform: 'none', px: 1.4, fontWeight: 700 } }}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="active">Active</ToggleButton>
          <ToggleButton value="expiring">Expiring</ToggleButton>
          <ToggleButton value="expired">Expired</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {loading ? (
        <LoadingState rows={6} />
      ) : visible.length === 0 ? (
        <EmptyState
          title={policies.length ? 'No policies match your filters' : 'No insurance policies yet'}
          description={policies.length ? 'Try a different search or status.' : 'Create your first policy to get started.'}
          actionLabel={policies.length ? undefined : 'New policy'}
          onAction={policies.length ? undefined : () => setDialog({ open: true, mode: 'create', policy: null })}
        />
      ) : (
        <Stack spacing={1}>
          {visible.map((policy) => (
            <PolicyCard
              key={policy.id}
              policy={policy}
              onEdit={(p) => setDialog({ open: true, mode: 'edit', policy: p })}
              onCovered={openCovered}
              onDelete={(p) => setDeleteTarget(p)}
            />
          ))}
        </Stack>
      )}

      <PolicyDialog
        open={dialog.open}
        mode={dialog.mode}
        initial={dialog.policy}
        onClose={() => setDialog({ open: false, mode: 'create', policy: null })}
        onSave={savePolicy}
      />

      <CoveredDialog open={covered.open} policy={covered.policy} rows={covered.rows} onClose={() => setCovered({ open: false, policy: null, rows: null })} />

      <Dialog open={Boolean(deleteTarget)} onClose={() => (deleting ? null : setDeleteTarget(null))} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete policy</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Delete <strong>{deleteTarget?.name}</strong>? This also removes its {Number(deleteTarget?.assignments_count) || 0} assignment(s). This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={confirmDelete} disabled={deleting} startIcon={<CloseIcon />}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
}
